import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { vendorsAPI, projectsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Store, Package, ShoppingCart, TrendingUp, Plus, Trash2, Pencil, Loader2, Clock,
  MapPin, Mail, Phone, User, FolderOpen, History, Search
} from 'lucide-react';

const UNITS = ['pc', 'sqft', 'sqm', 'ft', 'm', 'kg', 'ltr', 'set', 'roll', 'box', 'lot', 'other'];

export default function VendorDetail() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;

  const [vendor, setVendor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articleSearch, setArticleSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');

  // Article form
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const emptyArticle = { part_number: '', name: '', description: '', unit: 'pc', current_price: '', lead_time: '', min_order_qty: '' };
  const [articleForm, setArticleForm] = useState(emptyArticle);
  const [articleLoading, setArticleLoading] = useState(false);

  // Price history
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedArticleForPrice, setSelectedArticleForPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceForm, setPriceForm] = useState({ price: '', note: '', update_current: false });
  const [priceLoading, setPriceLoading] = useState(false);

  // Order form
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const emptyOrder = { project_id: '', items: [{ article_id: '', part_number: '', name: '', quantity: 1, unit_price: '' }], notes: '', status: 'placed' };
  const [orderForm, setOrderForm] = useState(emptyOrder);
  const [orderLoading, setOrderLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [vendorRes, articlesRes, ordersRes, projectsRes] = await Promise.all([
        vendorsAPI.get(vendorId),
        vendorsAPI.listArticles(vendorId),
        vendorsAPI.listVendorOrders(vendorId),
        projectsAPI.list(),
      ]);
      setVendor(vendorRes.data);
      setArticles(articlesRes.data);
      setOrders(ordersRes.data);
      setProjects(projectsRes.data);
    } catch (err) {
      console.error('Failed to load vendor:', err);
      navigate('/vendors');
    } finally {
      setLoading(false);
    }
  }, [vendorId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ===== Article handlers =====
  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    setArticleLoading(true);
    try {
      if (editingArticle) {
        const res = await vendorsAPI.updateArticle(editingArticle.article_id, articleForm);
        setArticles(articles.map(a => a.article_id === editingArticle.article_id ? res.data : a));
      } else {
        const res = await vendorsAPI.createArticle(vendorId, articleForm);
        setArticles([res.data, ...articles]);
      }
      setArticleDialogOpen(false);
      setEditingArticle(null);
      setArticleForm(emptyArticle);
    } catch (err) { console.error(err); } finally { setArticleLoading(false); }
  };

  const openEditArticle = (a) => {
    setEditingArticle(a);
    setArticleForm({
      part_number: a.part_number || '', name: a.name || '', description: a.description || '',
      unit: a.unit || 'pc', current_price: a.current_price || '', lead_time: a.lead_time || '',
      min_order_qty: a.min_order_qty || '',
    });
    setArticleDialogOpen(true);
  };

  const handleDeleteArticle = async (id) => {
    try {
      await vendorsAPI.deleteArticle(id);
      setArticles(articles.filter(a => a.article_id !== id));
    } catch (err) { console.error(err); }
  };

  // ===== Price history handlers =====
  const openPriceHistory = async (article) => {
    setSelectedArticleForPrice(article);
    setPriceForm({ price: '', note: '', update_current: false });
    setPriceDialogOpen(true);
    try {
      const res = await vendorsAPI.getPriceHistory(article.article_id);
      setPriceHistory(res.data);
    } catch (err) { console.error(err); }
  };

  const handleAddPrice = async (e) => {
    e.preventDefault();
    if (!selectedArticleForPrice) return;
    setPriceLoading(true);
    try {
      await vendorsAPI.addPriceHistory(selectedArticleForPrice.article_id, priceForm);
      const res = await vendorsAPI.getPriceHistory(selectedArticleForPrice.article_id);
      setPriceHistory(res.data);
      if (priceForm.update_current) {
        setArticles(articles.map(a =>
          a.article_id === selectedArticleForPrice.article_id ? { ...a, current_price: parseFloat(priceForm.price) } : a
        ));
      }
      setPriceForm({ price: '', note: '', update_current: false });
    } catch (err) { console.error(err); } finally { setPriceLoading(false); }
  };

  // ===== Order handlers =====
  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setOrderLoading(true);
    try {
      if (editingOrder) {
        const res = await vendorsAPI.updateOrder(editingOrder.order_id, orderForm);
        setOrders(orders.map(o => o.order_id === editingOrder.order_id ? res.data : o));
      } else {
        const res = await vendorsAPI.createOrder(vendorId, orderForm);
        setOrders([res.data, ...orders]);
      }
      setOrderDialogOpen(false);
      setEditingOrder(null);
      setOrderForm(emptyOrder);
    } catch (err) { console.error(err); } finally { setOrderLoading(false); }
  };

  const addOrderItem = () => {
    setOrderForm({ ...orderForm, items: [...orderForm.items, { article_id: '', part_number: '', name: '', quantity: 1, unit_price: '' }] });
  };

  const updateOrderItem = (idx, field, value) => {
    const items = [...orderForm.items];
    items[idx] = { ...items[idx], [field]: value };
    // Auto-fill from article
    if (field === 'article_id' && value) {
      const article = articles.find(a => a.article_id === value);
      if (article) {
        items[idx].part_number = article.part_number;
        items[idx].name = article.name;
        items[idx].unit_price = article.current_price;
      }
    }
    setOrderForm({ ...orderForm, items });
  };

  const removeOrderItem = (idx) => {
    setOrderForm({ ...orderForm, items: orderForm.items.filter((_, i) => i !== idx) });
  };

  const handleDeleteOrder = async (id) => {
    try {
      await vendorsAPI.deleteOrder(id);
      setOrders(orders.filter(o => o.order_id !== id));
    } catch (err) { console.error(err); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const formatCurrency = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN')}` : '—';
  const orderTotal = (items) => items.reduce((sum, i) => sum + (parseFloat(i.unit_price || 0) * parseInt(i.quantity || 0)), 0);

  const filteredArticles = articles.filter(a => {
    const term = articleSearch.toLowerCase();
    return !term || a.name?.toLowerCase().includes(term) || a.part_number?.toLowerCase().includes(term) || a.description?.toLowerCase().includes(term);
  });

  const filteredOrders = orders.filter(o => {
    const term = orderSearch.toLowerCase();
    if (!term) return true;
    return o.order_id?.toLowerCase().includes(term) ||
      o.project_name?.toLowerCase().includes(term) ||
      o.status?.toLowerCase().includes(term) ||
      (o.items || []).some(i => i.name?.toLowerCase().includes(term) || i.part_number?.toLowerCase().includes(term));
  });

  if (loading) {
    return <Layout><div className="animate-pulse space-y-4"><div className="h-8 bg-muted w-1/3 rounded" /><div className="h-48 bg-muted rounded" /></div></Layout>;
  }
  if (!vendor) {
    return <Layout><div className="text-center py-12"><p className="text-muted-foreground">Vendor not found</p></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/vendors" className="hover:text-foreground transition-colors">Vendor Directory</Link>
          <span>/</span>
          <span className="text-foreground">{vendor.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight">{vendor.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="capitalize px-2 py-0.5 rounded-full bg-muted text-xs">{vendor.category}</span>
              {vendor.contact_person && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{vendor.contact_person}</span>}
              {vendor.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{vendor.phone}</span>}
              {vendor.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{vendor.email}</span>}
              {vendor.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{vendor.address}</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Card><CardContent className="p-3"><p className="text-2xl font-light">{articles.length}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Articles</p></CardContent></Card>
            <Card><CardContent className="p-3"><p className="text-2xl font-light">{orders.length}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Orders</p></CardContent></Card>
            <Card><CardContent className="p-3"><p className="text-2xl font-light">{vendor.linked_projects?.length || 0}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Projects</p></CardContent></Card>
          </div>
        </div>

        {/* Linked Projects */}
        {vendor.linked_projects?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {vendor.linked_projects.map(p => (
              <Link key={p.project_id} to={`/projects/${p.project_id}`} className="flex items-center gap-1 text-xs px-2.5 py-1 bg-muted hover:bg-muted/80 rounded-full transition-colors">
                <FolderOpen className="h-3 w-3" />{p.name} — {p.city}
              </Link>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="articles" className="space-y-4">
          <TabsList>
            <TabsTrigger value="articles" data-testid="articles-tab"><Package className="h-4 w-4 mr-2" />Articles ({articles.length})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab"><ShoppingCart className="h-4 w-4 mr-2" />Orders ({orders.length})</TabsTrigger>
          </TabsList>

          {/* ===== ARTICLES TAB ===== */}
          <TabsContent value="articles" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-lg font-medium">Product Catalog</h3>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search articles..."
                    value={articleSearch}
                    onChange={e => setArticleSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="article-search-input"
                  />
                </div>
                {canEdit && (
                  <Button onClick={() => { setArticleForm(emptyArticle); setEditingArticle(null); setArticleDialogOpen(true); }} data-testid="add-article-btn">
                    <Plus className="h-4 w-4 mr-2" />Add Article
                  </Button>
                )}
              </div>
            </div>
            {filteredArticles.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Package className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{articleSearch ? 'No articles match your search' : 'No articles added yet'}</p></CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Part #</TableHead><TableHead>Name</TableHead><TableHead>Unit</TableHead>
                        <TableHead className="text-right">Price</TableHead><TableHead>Lead Time</TableHead><TableHead>Min Qty</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredArticles.map(a => (
                        <TableRow key={a.article_id} data-testid={`article-${a.article_id}`}>
                          <TableCell className="font-mono text-sm">{a.part_number || '—'}</TableCell>
                          <TableCell>
                            <div><p className="font-medium">{a.name}</p>{a.description && <p className="text-xs text-muted-foreground line-clamp-1">{a.description}</p>}</div>
                          </TableCell>
                          <TableCell className="text-sm">{a.unit}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(a.current_price)}</TableCell>
                          <TableCell className="text-sm">{a.lead_time || '—'}</TableCell>
                          <TableCell className="text-sm">{a.min_order_qty || '—'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPriceHistory(a)} title="Price History" data-testid={`price-history-${a.article_id}`}>
                                <TrendingUp className="h-3.5 w-3.5" />
                              </Button>
                              {canEdit && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditArticle(a)}><Pencil className="h-3.5 w-3.5" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteArticle(a.article_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== ORDERS TAB ===== */}
          <TabsContent value="orders" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-lg font-medium">Purchase Orders</h3>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search orders..."
                    value={orderSearch}
                    onChange={e => setOrderSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="order-search-input"
                  />
                </div>
                {canEdit && (
                  <Button onClick={() => { setOrderForm(emptyOrder); setEditingOrder(null); setOrderDialogOpen(true); }} data-testid="add-order-btn">
                    <Plus className="h-4 w-4 mr-2" />New Order
                  </Button>
                )}
              </div>
            </div>
            {filteredOrders.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><ShoppingCart className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">{orderSearch ? 'No orders match your search' : 'No orders placed yet'}</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map(o => (
                  <Card key={o.order_id} data-testid={`order-${o.order_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${o.status === 'delivered' ? 'bg-green-100 text-green-700' : o.status === 'placed' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {o.status}
                          </span>
                          <span className="text-sm font-medium">{o.order_id}</span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(o.ordered_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/projects/${o.project_id}`} className="text-xs px-2 py-0.5 bg-muted rounded-full hover:bg-muted/80 flex items-center gap-1">
                            <FolderOpen className="h-3 w-3" />{o.project_name}
                          </Link>
                          <span className="font-medium">{formatCurrency(o.total_amount)}</span>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteOrder(o.order_id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Part #</TableHead><TableHead className="text-xs">Item</TableHead>
                            <TableHead className="text-xs text-right">Qty</TableHead><TableHead className="text-xs text-right">Unit Price</TableHead>
                            <TableHead className="text-xs text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(o.items || []).map((item, idx) => (
                            <TableRow key={`${o.order_id}-item-${item.article_id || idx}`}>
                              <TableCell className="font-mono text-xs">{item.part_number || '—'}</TableCell>
                              <TableCell className="text-sm">{item.name}</TableCell>
                              <TableCell className="text-right text-sm">{item.quantity}</TableCell>
                              <TableCell className="text-right text-sm">{formatCurrency(parseFloat(item.unit_price || 0))}</TableCell>
                              <TableCell className="text-right font-medium text-sm">{formatCurrency(parseFloat(item.unit_price || 0) * parseInt(item.quantity || 0))}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {o.notes && <p className="text-xs text-muted-foreground mt-2">Note: {o.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== ARTICLE DIALOG ===== */}
        <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editingArticle ? 'Edit Article' : 'Add Article'}</DialogTitle></DialogHeader>
            <form onSubmit={handleArticleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Part Number</Label><Input value={articleForm.part_number} onChange={e => setArticleForm({ ...articleForm, part_number: e.target.value })} placeholder="e.g., FLR-001" data-testid="article-part-number" /></div>
                  <div className="space-y-2"><Label>Name</Label><Input value={articleForm.name} onChange={e => setArticleForm({ ...articleForm, name: e.target.value })} required placeholder="e.g., Italian Marble Tile" data-testid="article-name" /></div>
                </div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={articleForm.description} onChange={e => setArticleForm({ ...articleForm, description: e.target.value })} rows={2} placeholder="Specifications, dimensions..." /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Unit</Label>
                    <Select value={articleForm.unit} onValueChange={v => setArticleForm({ ...articleForm, unit: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" step="0.01" value={articleForm.current_price} onChange={e => setArticleForm({ ...articleForm, current_price: e.target.value })} required data-testid="article-price" /></div>
                  <div className="space-y-2"><Label>Lead Time</Label><Input value={articleForm.lead_time} onChange={e => setArticleForm({ ...articleForm, lead_time: e.target.value })} placeholder="e.g., 2 weeks" /></div>
                </div>
                <div className="space-y-2"><Label>Min Order Qty</Label><Input type="number" value={articleForm.min_order_qty} onChange={e => setArticleForm({ ...articleForm, min_order_qty: e.target.value })} placeholder="Optional" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setArticleDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={articleLoading} data-testid="save-article-btn">{articleLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingArticle ? 'Update' : 'Add Article'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ===== PRICE HISTORY DIALOG ===== */}
        <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Price History</DialogTitle>
              <DialogDescription>{selectedArticleForPrice?.name} ({selectedArticleForPrice?.part_number})</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Manual price entry */}
              {canEdit && (
                <form onSubmit={handleAddPrice} className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Price (₹)</Label>
                    <Input type="number" step="0.01" value={priceForm.price} onChange={e => setPriceForm({ ...priceForm, price: e.target.value })} required placeholder="New price" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Note</Label>
                    <Input value={priceForm.note} onChange={e => setPriceForm({ ...priceForm, note: e.target.value })} placeholder="Optional note" />
                  </div>
                  <div className="flex items-center gap-2 pb-0.5">
                    <label className="text-xs flex items-center gap-1 whitespace-nowrap">
                      <input type="checkbox" checked={priceForm.update_current} onChange={e => setPriceForm({ ...priceForm, update_current: e.target.checked })} />
                      Set as current
                    </label>
                    <Button type="submit" size="sm" disabled={priceLoading} data-testid="add-price-btn">
                      {priceLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    </Button>
                  </div>
                </form>
              )}
              {/* Price history list */}
              <div className="max-h-64 overflow-y-auto border rounded-md">
                {priceHistory.length === 0 ? (
                  <p className="p-4 text-sm text-center text-muted-foreground">No price history recorded</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs text-right">Price</TableHead><TableHead className="text-xs">Note</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.map(h => (
                        <TableRow key={h.history_id}>
                          <TableCell className="text-xs"><span className="flex items-center gap-1"><History className="h-3 w-3 text-muted-foreground" />{formatDate(h.recorded_at)}</span></TableCell>
                          <TableCell className="text-right font-medium text-sm">{formatCurrency(h.price)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{h.note || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== ORDER DIALOG ===== */}
        <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOrder ? 'Edit Order' : 'New Purchase Order'}</DialogTitle>
              <DialogDescription>Create a purchase order for {vendor.name}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleOrderSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={orderForm.project_id || 'none'} onValueChange={v => setOrderForm({ ...orderForm, project_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="order-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select a project</SelectItem>
                        {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={orderForm.status} onValueChange={v => setOrderForm({ ...orderForm, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placed">Placed</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Order items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Order Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOrderItem}><Plus className="h-3 w-3 mr-1" />Add Item</Button>
                  </div>
                  <div className="space-y-2">
                    {orderForm.items.map((item, idx) => (
                      <div key={`order-item-${idx}`} className="flex gap-2 items-start p-3 border rounded-md bg-muted/20">
                        <div className="flex-1 space-y-2">
                          <Select value={item.article_id || 'custom'} onValueChange={v => updateOrderItem(idx, 'article_id', v === 'custom' ? '' : v)}>
                            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select article or custom" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="custom">Custom item</SelectItem>
                              {articles.map(a => <SelectItem key={a.article_id} value={a.article_id}>{a.part_number} — {a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {!item.article_id && (
                            <div className="grid grid-cols-2 gap-2">
                              <Input className="h-8 text-sm" placeholder="Part #" value={item.part_number} onChange={e => updateOrderItem(idx, 'part_number', e.target.value)} />
                              <Input className="h-8 text-sm" placeholder="Item name" value={item.name} onChange={e => updateOrderItem(idx, 'name', e.target.value)} />
                            </div>
                          )}
                        </div>
                        <div className="w-20"><Input className="h-8 text-sm" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateOrderItem(idx, 'quantity', e.target.value)} /></div>
                        <div className="w-28"><Input className="h-8 text-sm" type="number" step="0.01" placeholder="Unit ₹" value={item.unit_price} onChange={e => updateOrderItem(idx, 'unit_price', e.target.value)} /></div>
                        <div className="w-24 text-right text-sm font-medium pt-1.5">{formatCurrency(parseFloat(item.unit_price || 0) * parseInt(item.quantity || 0))}</div>
                        {orderForm.items.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeOrderItem(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-right font-medium">Total: {formatCurrency(orderTotal(orderForm.items))}</div>
                </div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={orderForm.notes} onChange={e => setOrderForm({ ...orderForm, notes: e.target.value })} rows={2} placeholder="Delivery instructions, special requirements..." /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={orderLoading || !orderForm.project_id} data-testid="save-order-btn">
                  {orderLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingOrder ? 'Update' : 'Place Order'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
